import { createContext, useCallback, useEffect, useState } from "react";
import profileApi from "../services/profileApi";
import { isMongolia } from "../utils/country";

export const ProfileContext = createContext();

export const ProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch profile from hrSystemServer using JWT token
   * Works for both phone-based auth and Telegram auth
   */
  const fetchProfile = useCallback(async () => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("chatToken="));

    // Skip if no token
    if (!token) {
      setLoading(false);
      setProfile(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      let response = await profileApi.getMyProfile();

      // If JWT auth failed, fall back to phone-based lookup
      if (!response.found) {
        const phone = profileApi.getPhoneFromToken();
        if (phone) {
          response = await profileApi.getProfileByPhone(phone);
        }
      }

      if (response.found) {
        setProfile(response.candidate);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
      setError("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create or update profile
   * Uses PUT /me endpoint (JWT auth) - works for both phone and Telegram users
   * @param {Object} profileData - Profile data (phone optional for Telegram users)
   * @returns {Promise<boolean>} Success status
   */
  const saveProfile = useCallback(async (profileData) => {
    try {
      setSaving(true);
      setError(null);

      const dataToSend = {
        name: profileData.name || null,
        lastName: profileData.lastName || null,
        email: profileData.email || null,
        gender: profileData.gender || null,
        regNumber: profileData.regNumber || null,
        birthdate: profileData.birthdate && profileData.birthdate.match(/^\d{4}-\d{2}-\d{2}$/)
          ? profileData.birthdate
          : null,
      };

      if (profileData.phone) {
        dataToSend.phone = profileData.phone;
      }

      // Try JWT-based endpoint first
      let response = await profileApi.updateMyProfile(dataToSend);

      // If JWT auth failed, fall back to phone-based endpoint
      if (!response.found && response.message === "Not authenticated") {
        const phone = profile?.phoneNumber || profileApi.getPhoneFromToken();
        if (phone) {
          dataToSend.phone = phone;
          response = await profileApi.createOrUpdateProfile(dataToSend);
        }
      }

      if (response.found && response.candidate) {
        setProfile(response.candidate);
        return true;
      }
      console.error("Profile save failed:", response.message || "Unknown error");
      return false;
    } catch (err) {
      console.error("Error saving profile:", err?.response?.data || err.message);
      setError("Failed to save profile");
      return false;
    } finally {
      setSaving(false);
    }
  }, [profile]);

  /**
   * Refresh profile data
   * @returns {Promise} Resolves when profile is fetched
   */
  const refreshProfile = useCallback(() => {
    return fetchProfile(true);
  }, [fetchProfile]);

  /**
   * Update profile directly from API response data
   * Used after photo upload to immediately reflect changes without re-fetching
   */
  const updateProfile = useCallback((profileData) => {
    setProfile(profileData);
  }, []);

  /**
   * Clear profile (on logout)
   */
  const clearProfile = useCallback(() => {
    setProfile(null);
    setError(null);
  }, []);

  // Fetch profile on mount if token exists
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  /**
   * Check if profile is ready (not loading)
   * Profile check happens after login via refreshProfile() in SignUp.jsx
   */
  const profileReady = !loading;

  // isMongolia imported from ../utils/country at top of file

  /**
   * Check if user logged in via Telegram (has all profile info from Telegram)
   * Includes all Telegram auth methods: Mini App, Widget, and Bot
   */
  const isTelegramUser = profile && (
    profile.authMethod === "TELEGRAM" ||
    profile.authMethod === "TELEGRAM_WIDGET" ||
    profile.authMethod === "TELEGRAM_WEBAPP" ||
    profile.authMethod === "TELEGRAM_BOT"
  );

  /**
   * Check if user has telegramId (from Telegram auth)
   * Used for telegramId-alternative approach - allows chat without phone
   */
  const hasTelegramId = profile && profile.telegramId;

  /**
   * All fields are required to chat.
   * regNumber is only required for Mongolia (chat.oneplace.hr).
   */
  const needsPhone = profileReady && profile && !profile.phoneNumber && !hasTelegramId;
  const needsNameCollection = profileReady && profile && (!profile.name || !profile.lastName);
  const needsProfileCompletion = profileReady && profile && !profile.photoUrl;
  const needsEmail = profileReady && profile && !profile.email;
  const needsGender = profileReady && profile && !profile.gender;
  const needsBirthdate = profileReady && profile && !profile.birthdate;
  const needsRegNumber = profileReady && profile && isMongolia && !profile.regNumber;

  return (
    <ProfileContext.Provider
      value={{
        profile,
        loading,
        saving,
        error,
        saveProfile,
        refreshProfile,
        updateProfile,
        clearProfile,
        needsPhone,
        needsNameCollection,
        needsProfileCompletion,
        needsEmail,
        needsGender,
        needsBirthdate,
        needsRegNumber,
        profileReady,
        isTelegramUser,
        hasTelegramId,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};
